import { RenameAttributesSectionCommandV1 } from '@Alternatives/AlternativesLists/Domain/commands-core';
import AlternativesListsCommands from '@Alternatives/AlternativesLists/Domain/commands-vue-component';
import { AttributeDeleterDialogViewModel } from "@Alternatives/Attributes/Components/AttributeDeleterDialog";
import AttributeDeleterDialog from '@Alternatives/Attributes/Components/AttributeDeleterDialog.vue';
import { AttributeDependenciesDialogViewModel } from "@Alternatives/Attributes/Components/AttributeDependenciesDialog";
import AttributeDependenciesDialog from '@Alternatives/Attributes/Components/AttributeDependenciesDialog.vue';
import { AttributeEditorDialogViewModel } from "@Alternatives/Attributes/Components/AttributeEditorDialog";
import AttributeEditorDialog from '@Alternatives/Attributes/Components/AttributeEditorDialog.vue';
import { AttributesSectionEditorViewModel } from "@Alternatives/Attributes/Components/AttributesSectionEditor";
import AttributesSectionEditor from '@Alternatives/Attributes/Components/AttributesSectionEditor.vue';
import { DeleteAttributeCommandV1 } from '@Alternatives/Attributes/Domain/commands-core';
import AttributesCommands from '@Alternatives/Attributes/Domain/commands-vue-component';
import AttributesQueries from '@Alternatives/Attributes/Domain/queries-vue-component';
import { AttributeDependenciesReadModel, AttributeEditorReadModel, AttributeReadModel, AttributesListReadModel } from '@Alternatives/Attributes/Domain/readmodels';
import { ICommandBase } from '@Alternatives/cqrs';
import { dateCompare1, generateName, strCompareLowerCase } from '@common/Common';
import PortfolioOrDecisionNavigation from "@common/Components/PortfolioOrDecisionNavigation.vue";
import { dynamicUiRoutes } from '@common/dynamicUiRoutes';
import moment from "moment";
import { Component, mixins } from 'vue-facing-decorator';

@Component({
    components: {
        PortfolioOrDecisionNavigation, AttributesSectionEditor, AttributeDependenciesDialog, AttributeDeleterDialog, AttributeEditorDialog
    }
})
export default class AttributesPage extends mixins(AttributesQueries, AttributesCommands, AlternativesListsCommands)
{
    get workspaceId(): number { return Number.parseInt(this.$route.params.workspaceId); }
    get projectId(): number { return Number.parseInt(this.$route.params.projectId); }

    rm: AttributesListReadModel | null = null;
    get alternativesListId() { return this.rm!.AlternativesListId; }
    get attributes() { return this.rm?.Attributes ?? []; }

    searchPhrase: string | null = null;
    collapseAll: boolean = true;
    sections: SectionVM[] = [];

    attrDependenciesDialog = new AttributeDependenciesDialogViewModel();
    attrDeleterDialog = new AttributeDeleterDialogViewModel();
    attributeEditorDialog: AttributeEditorDialogViewModel = new AttributeEditorDialogViewModel();
    attributesSectionEditor = new AttributesSectionEditorViewModel();

    get sectionsView(): SectionVM[] 
    {
        return this.sections.sort((a, b) => strCompareLowerCase(a.Name, b.Name));
    }

    getModifiedDateStr(rm: AttributeReadModel): string 
    {
        return moment(rm.Modified).format('MMM D, YYYY');
    }

    getModifiedTimeStr(rm: AttributeReadModel): string
    {
        return moment(rm.Modified).format('LT');
    }

    url_CreateWeightedScorePage(attributeId: number): string
    {
        return dynamicUiRoutes.Criteria_CreateWeightedScorePage2(this.$route, this.workspaceId, this.projectId, attributeId);
    }

    url_EditWeightedScorePage(attributeId: number): string
    {
        return dynamicUiRoutes.Criteria_EditWeightedScorePage2(this.$route, this.workspaceId, this.projectId, attributeId);
    }


    buildSections() 
    {
        // Create a map to hold sections by their names
        const sectionsMap = new Map();

        // Iterate over attributes and populate the map
        // This automatically ensures that each section name is unique
        this.attributes.forEach(attribute =>
        {
            if (!sectionsMap.has(attribute.Section))
            {
                sectionsMap.set(attribute.Section, new SectionVM(this, attribute.Section));
            }
        });

        // Replace the Sections array with new values from the map
        // This filters out empty sections and adds new ones
        this.sections = Array.from(sectionsMap.values());
    }

    toggleExpandAll()
    {
        if (this.collapseAll === true)
        {
            this.sections.forEach(s => s.Expanded = false);
        }
        else
        {
            this.sections.forEach(s => s.Expanded = true);
        }

        this.collapseAll = !this.collapseAll;
    }



    showAttributeCreator(section: string | null)
    {
        this.attributeEditorDialog.showCreator({
            defaultName: generateName("Attribute"),
            defaultSection: section,

            allowChangeAttributeType: true,
            allowInputTypeEvaluate: true,
            allowMultipleValuesEnabled: true,
        });

        this.runAttributeEditorQueryV1(this.workspaceId, this.projectId, null);
    }

    showAttributeCloner(rm: AttributeReadModel)
    {
        if (rm.IsWeightedScore === true)
        {
            location.href = this.url_CreateWeightedScorePage(rm.Id);
        }
        else
        {
            this.attributeEditorDialog.showCloner(rm.Id, rm.Name);
            this.runAttributeEditorQueryV1(this.workspaceId, this.projectId, rm.Id);
        }
    }

    showAttributeEditor(rm: AttributeReadModel)
    {
        if (rm.IsWeightedScore === true)
        {
            location.href = this.url_EditWeightedScorePage(rm.Id);
        }
        else
        {
            this.attributeEditorDialog.showEditor(rm.Id, rm.Name);
            this.runAttributeEditorQueryV1(this.workspaceId, this.projectId, rm.Id);
        }
    }



    showAttributeDeleter(attrId: number)
    {
        const rm = this.rm.Attributes.find(a => a.Id === attrId);
        this.attrDeleterDialog.show(rm.Id, rm.Name);
        this.runAttributeDependenciesQueryV1(this.workspaceId, this.projectId, this.alternativesListId, rm.Id);
    }

    showAttributeDependenciesViewer(attrId: number)
    {
        this.attrDeleterDialog.close();

        const rm = this.rm.Attributes.find(a => a.Id === attrId);
        this.attrDependenciesDialog.show(rm.Id, rm.Name);
        this.runAttributeDependenciesQueryV1(this.workspaceId, this.projectId, this.alternativesListId, attrId);
    }

    showAttributesSectionEditor(s: SectionVM)
    {
        this.attributesSectionEditor.show(s.Name)
    }

    attributeCreated(attrId: number)
    {
        this.runAttributeByIdQueryV1(this.workspaceId, this.projectId, this.alternativesListId, attrId);
    }

    attributeUpdated(attrId: number)
    {
        this.runAttributeByIdQueryV1(this.workspaceId, this.projectId, this.alternativesListId, attrId);
    }


    handleDeleteAttributeCommand(attrId: number)
    {
        this.invokeDeleteAttributeCommandV1(this.workspaceId, this.projectId, this.rm.AlternativesListId, attrId)
    }

    handleRenameAttributesSectionCommand(newName: string | null, oldName: string | null)
    {
        this.invokeRenameAttributesSectionCommandV1(this.workspaceId, this.projectId, this.alternativesListId, oldName, newName);
    }



    mounted()
    {
        document.title = "Attributes | TransparentChoice";
        const includeAll = this.$route.params.include === 'all';
        this.runAttributesListByProjectIdQueryV1(this.workspaceId, this.projectId, includeAll);
    }

    onSetModel(model: any, modelName: string) 
    {
        if (modelName === "AttributesListReadModel")
        {
            this.rm = model as AttributesListReadModel;
            this.buildSections();
        }
        else if (modelName === "AttributeReadModel")
        {
            const rm = model as AttributeReadModel;
            const curr = this.attributes.find(a => a.Id === rm.Id);

            if (curr != null)
            {
                this.attributes.remove(curr);
            }

            this.attributes.push(rm);
            this.buildSections();
        }
        else if (modelName === "AttributeDependenciesReadModel")
        {
            this.attrDependenciesDialog.setData(model as AttributeDependenciesReadModel);
            this.attrDeleterDialog.setData(model as AttributeDependenciesReadModel);
        }
        else if (modelName === "AttributeEditorReadModel")
        {
            const rm = model as AttributeEditorReadModel;
            this.attributeEditorDialog.setData(rm);
        }
    }

    onCommandSucceed(cmd: ICommandBase, correlationId: string, result: any)
    {
        if (DeleteAttributeCommandV1.matchType(cmd))
        {
            const removed = this.attributes.find(a => a.Id === Number(correlationId));
            this.attributes.remove(removed);
            this.buildSections();

            this.attrDeleterDialog.close();
        }
        else if (RenameAttributesSectionCommandV1.matchType(cmd))
        {
            const cmd1 = cmd as RenameAttributesSectionCommandV1;

            const section = this.sections.find(s => s.Name === cmd1.OldName);

            if (section)
            {
                section.attributes.forEach(a => a.Section = cmd1.NewName);
                this.buildSections();
            }

            this.attributesSectionEditor.close();
        }
    }
}

class SectionVM
{
    page: AttributesPage = null;

    Name: string;
    Expanded: boolean = true;
    OrderBy: string = "name";
    OrderByDescending: boolean = false;

    get attributes() { return this.page.attributes?.filter(a => a.Section?.toLowerCase() === this.Name?.toLowerCase()) ?? []; }

    get AttributesView()
    {
        let view = new Array(...this.attributes);

        if (this.page.searchPhrase)
        {
            view = view.filter(o => o.Name.toLowerCase().indexOf(this.page.searchPhrase.toLowerCase()) >= 0);
        }

        if (this.OrderBy === "name")
        {
            view = view.sort((a, b) => strCompareLowerCase(a.Name, b.Name));
        }
        else if (this.OrderBy === "modified")
        {
            view = view.sort((a, b) => dateCompare1(a.Modified, b.Modified));
        }

        if (this.OrderByDescending === true)
        {
            view = view.reverse();
        }

        return view;
    }

    constructor(page: AttributesPage, name: string)
    {
        this.page = page;
        this.Name = name;
    }

    orderByName()
    {
        if (this.OrderBy === 'name')
        {
            this.OrderByDescending = !this.OrderByDescending;
        }
        else
        {
            this.OrderByDescending = false;
            this.OrderBy = 'name';
        }
    }

    orderByModified()
    {
        if (this.OrderBy === 'modified')
        {
            this.OrderByDescending = !this.OrderByDescending;
        }
        else
        {
            this.OrderByDescending = true;
            this.OrderBy = 'modified';
        }
    }
}